import { useEffect, useRef, useState } from "react";
import { Card } from "./ui/card";
import { Bell, Check, Calendar, ClipboardCheck, Clock, ChevronDown, ChevronUp } from "lucide-react";
import api from "../lib/api";

const ICON = {
  reminder: Clock,
  event_created: Calendar,
  event_approved: Check,
  approval_required: ClipboardCheck,
  grade_request: ClipboardCheck,
};

function NotifCard({ n, onAutoRead }) {
  const ref = useRef(null);
  const seen = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        seen.current = true;
      } else if (seen.current) {
        obs.disconnect();
        onAutoRead(n.id);
      }
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [n.id, onAutoRead]);

  const Icon = ICON[n.type] || Bell;
  return (
    <Card ref={ref} data-testid={`notification-${n.id}`}
      className="glass-card p-4 flex items-start gap-3">
      <Icon className="w-5 h-5 text-[var(--sn-gold)] mt-0.5 shrink-0" />
      <div className="flex-1">
        <p className="text-neutral-200 text-sm">{n.message}</p>
        <div className="text-[11px] text-neutral-500 mt-1 font-mono">
          {new Date(n.created_at).toLocaleString()}
        </div>
      </div>
    </Card>
  );
}

export default function NotificationsView({ notifications, onRefresh }) {
  const [showRead, setShowRead] = useState(false);
  const unread = notifications.filter(n => !n.read);
  const read = notifications.filter(n => n.read);

  const autoRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      onRefresh();
    } catch { /* silent */ }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl text-[var(--sn-gold)]">Notifications</h2>
        <p className="text-neutral-500 text-sm">Scroll past a notification and it files itself into Read. Automated reminders fire 2 weeks, 1 week, 3 days, and 1 day before each approved event.</p>
      </div>

      <Card className="glass-card p-4">
        <div className="text-xs uppercase tracking-widest text-neutral-500 mb-3">Reminder Schedule</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "2 Weeks Out", desc: "Save-the-date + headcount" },
            { label: "1 Week Out", desc: "Logistics + forms deadline" },
            { label: "3 Days Out", desc: "Compliance + dress code" },
            { label: "1 Day Out", desc: "Final schedule + address" },
          ].map(x => (
            <div key={x.label} className="rounded-lg border border-[var(--sn-gold)]/20 bg-[var(--sn-gold)]/5 p-3">
              <div className="text-[var(--sn-gold)] font-semibold text-sm">{x.label}</div>
              <div className="text-xs text-neutral-400 mt-1">{x.desc}</div>
            </div>
          ))}
        </div>
      </Card>

      <div className="space-y-2">
        <div className="text-xs uppercase tracking-widest text-neutral-500" data-testid="unread-header">
          Unread ({unread.length})
        </div>
        {unread.length === 0 ? (
          <Card className="glass-card p-8 text-center text-neutral-500">
            <Bell className="w-8 h-8 text-[var(--sn-gold)] mx-auto mb-2" />
            All caught up.
          </Card>
        ) : (
          unread.map(n => <NotifCard key={n.id} n={n} onAutoRead={autoRead} />)
        )}
      </div>

      {read.length > 0 && (
        <div className="space-y-2">
          <button data-testid="read-folder-toggle" onClick={() => setShowRead(!showRead)}
            className="flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-500 hover:text-[var(--sn-gold)] transition">
            {showRead ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            Read ({read.length})
          </button>
          {showRead && read.map(n => {
            const Icon = ICON[n.type] || Bell;
            return (
              <Card key={n.id} data-testid={`read-notification-${n.id}`}
                className="glass-card p-4 flex items-start gap-3 opacity-50">
                <Icon className="w-5 h-5 text-neutral-500 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="text-neutral-400 text-sm">{n.message}</p>
                  <div className="text-[11px] text-neutral-600 mt-1 font-mono">
                    {new Date(n.created_at).toLocaleString()}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
